import { useLayoutEffect, useState } from "react";

import {
  ArrowSize,
  arrowSizeMap,
} from "apps/website/components/feature/Tooltip/ArrowIndicator/ArrowIndicator";

/** *
 * Hook to calculate the offset of an arrow relative to an anchor element
 * @param anchorRef - The ref to the anchor element
 * @param containerRef - The ref to the container element
 * @param deps - The dependencies to watch for recalculation
 * @param arrowSize - The size of the arrow
 */
export function useArrowOffset<TAnchor extends HTMLElement = HTMLElement>(
  anchorRef: React.RefObject<TAnchor>,
  containerRef: React.RefObject<HTMLElement>,
  deps: React.DependencyList = [],
  arrowSize: ArrowSize = "small",
) {
  const [ offset, setOffset ] = useState<number | undefined>();

  useLayoutEffect(() => {
    const calculateOffset = () => {
      const anchorEl = anchorRef.current;
      const containerEl = containerRef.current;

      if (!anchorEl || !containerEl) {
        setOffset(undefined);
        return;
      }

      const anchorBox = anchorEl.getBoundingClientRect();
      const containerBox = containerEl.getBoundingClientRect();

      const arrowWidth = arrowSizeMap[arrowSize];
      const calculated =
        anchorBox.left - containerBox.left + anchorBox.width / 2 - arrowWidth / 2;

      setOffset(calculated);
    };

    calculateOffset();
    window.addEventListener("resize", calculateOffset);

    return () => window.removeEventListener("resize", calculateOffset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ anchorRef, containerRef, arrowSize, ...deps ]);

  return offset;
}
